import { FunctionComponent, useState } from "react";
import {
  FeaturedProduct,
  FeaturedProductProps,
  useSlideOverDialogContext,
} from "@bluebottlecoffee/design-system/components";
import { useCart } from "@chordcommerce/react-autonomy";
import useVariantPrice from "../lib/chord/hooks/components/use-variant-price";
import useVariantAvailability from "../lib/chord/hooks/components/use-variant-availability";
import { trackEvent } from "../lib/segment/track-event";
import { StaticFeaturedProductProps } from "../lib/transformers/featured-product";

export type FeaturedProductWrapperProps = {
  moduleProps: StaticFeaturedProductProps;
  /** Loading label shown on the add-to-cart button (e.g. "Adding..."). */
  loadingMessage: string;
};

export const FeaturedProductWrapper: FunctionComponent<
  FeaturedProductWrapperProps
> = ({ moduleProps, loadingMessage }) => {
  const { setActiveContentType } = useSlideOverDialogContext();
  const { addToCart, addSubscription } = useCart();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleAddToCart: FeaturedProductProps["addToCart"] = async (
    sku,
    quantity,
  ) => {
    try {
      setErrorMessage(null);
      setLoading(true);
      await addToCart({ sku, quantity });
      setActiveContentType("minicart");
    } catch (error) {
      setActiveContentType(null);
      setErrorMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe: FeaturedProductProps["subscribeProduct"] = async ({
    interval,
    quantity,
    sku,
  }) => {
    try {
      setErrorMessage(null);
      setLoading(true);
      await addSubscription({
        endDate: null,
        interval: { length: interval.length, unit: interval.unit },
        quantity,
        sku,
      });
      setActiveContentType("minicart");
    } catch (error) {
      setActiveContentType(null);
      setErrorMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FeaturedProduct
      {...moduleProps}
      subscribeProduct={handleSubscribe}
      addToCart={handleAddToCart}
      addToCartProps={{
        loading,
        loadingMessage,
        errorMessage: errorMessage || "",
      }}
      useVariantPrice={useVariantPrice}
      useVariantAvailability={useVariantAvailability}
      clickTracking={trackEvent}
    />
  );
};
